import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PropertyLocationForm } from "./PropertyLocationForm";
import { PropertyDetailsSection } from "../PropertyDetailsSection";
import { PropertyFinancialForm } from "./PropertyFinancialForm";
import { PropertyDescriptionForm } from "./PropertyDescriptionForm";

interface PropertyDetailsTabProps {
  id: string;
  property: any;
  isLoading: boolean;
  isDraft?: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSelectChange: (name: string, value: string) => void;
  handleStatusChange: (status: string) => void;
  handleDateChange: (date: Date | undefined) => void;
  handleSubmit: (e: React.FormEvent) => void;
  handleSaveDraft?: () => void;
  errors?: Record<string, string>;
  clearFieldError?: (field: string) => void;
}

export const PropertyDetailsTab = ({
  id,
  property,
  isLoading,
  isDraft,
  handleChange,
  handleSelectChange,
  handleStatusChange,
  handleDateChange,
  handleSubmit,
  handleSaveDraft,
  errors = {},
  clearFieldError,
}: PropertyDetailsTabProps) => {
  const navigate = useNavigate();
  
  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      <PropertyLocationForm
        address={property.address}
        city={property.city}
        state={property.state}
        zipCode={property.zipCode}
        onChange={handleChange}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      
      <PropertyDetailsSection
        bedrooms={property.bedrooms}
        setBedrooms={(value) => handleSelectChange("bedrooms", value)}
        bathrooms={property.bathrooms}
        setBathrooms={(value) => handleSelectChange("bathrooms", value)}
        squareFeet={property.squareFeet}
        setSquareFeet={(value) => handleSelectChange("squareFeet", value)}
        propertyType={property.propertyType}
        setPropertyType={(value) => handleSelectChange("propertyType", value)}
        units={property.units ?? ""}
        setUnits={(value) => handleSelectChange("units", value)}

        foundationType={property.foundationType}
        setFoundationType={(value) => handleSelectChange("foundationType", value)}
        yearBuilt={property.yearBuilt}
        setYearBuilt={(value) => handleSelectChange("yearBuilt", value)}
        lotSize={property.lotSize}
        setLotSize={(value) => handleSelectChange("lotSize", value)}
        roofAge={property.roofAge}
        setRoofAge={(value) => handleSelectChange("roofAge", value)}
        windowType={property.windowType}
        setWindowType={(value) => handleSelectChange("windowType", value)}
        furnaceAge={property.furnaceAge}
        setFurnaceAge={(value) => handleSelectChange("furnaceAge", value)}
        waterHeaterAge={property.waterHeaterAge}
        setWaterHeaterAge={(value) => handleSelectChange("waterHeaterAge", value)}
        architecture={property.architectureStyle}
        setArchitecture={(value) => handleSelectChange("architectureStyle", value)}
        parking={property.parkingType}
        setParking={(value) => handleSelectChange("parkingType", value)}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      
      <PropertyFinancialForm
        price={property.price}
        arv={property.arv}
        zillowLink={property.zillowLink}
        onChange={handleChange}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      
      <PropertyDescriptionForm
        description={property.description}
        onChange={handleChange}
      />
      
      <div className="flex justify-end space-x-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate(`/seller/properties/${id}`, { replace: true })}
        >
          Cancel
        </Button>
        {isDraft ? (
          handleSaveDraft && (
            <Button
              type="button"
              className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
              onClick={handleSaveDraft}
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : "Save Draft"}
            </Button>
          )
        ) : (
          <Button
            type="submit"
            className="bg-godyspo-gold hover:bg-godyspo-gold/90 text-black"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save Changes"}
          </Button>
        )}
      </div>
    </form>
  );
};
